import React, { useState } from "react";
import axios from "axios";
import { IoMdClose } from "react-icons/io";

const AddCourseModal = ({ isOpen, onClose, onCourseAdded }) => {
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    level: "Beginner",
    duration: "",
    description: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const res = await axios.post(
        "http://localhost:8000/api/course",
        formData
      );
      if (onCourseAdded) {
        onCourseAdded(res.data);
      }
      setFormData({
        title: "",
        category: "",
        level: "Beginner",
        duration: "",
        description: "",
      });
    } catch (err) {
      console.error(err);
      setError("Failed to add course. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError("");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-2xl bg-white p-4 shadow-2xl sm:p-6">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            Add New Course
          </h2>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <IoMdClose className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-4 overflow-y-auto pr-1"
        >
          {error && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {/* Title */}
            <div className="flex flex-col space-y-1.5">
              <label
                htmlFor="title"
                className="text-xs font-medium text-slate-600"
              >
                Course Title <span className="text-rose-500">*</span>
              </label>
              <input
                id="title"
                name="title"
                type="text"
                required
                placeholder="e.g. Full Stack Web Development"
                value={formData.title}
                onChange={handleChange}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            {/* Category */}
            <div className="flex flex-col space-y-1.5">
              <label
                htmlFor="category"
                className="text-xs font-medium text-slate-600"
              >
                Category
              </label>
              <input
                id="category"
                name="category"
                type="text"
                placeholder="e.g. Web Development"
                value={formData.category}
                onChange={handleChange}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {/* Level */}
            <div className="flex flex-col space-y-1.5">
              <label
                htmlFor="level"
                className="text-xs font-medium text-slate-600"
              >
                Level
              </label>
              <select
                id="level"
                name="level"
                value={formData.level}
                onChange={handleChange}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            {/* Duration */}
            <div className="flex flex-col space-y-1.5">
              <label
                htmlFor="duration"
                className="text-xs font-medium text-slate-600"
              >
                Duration
              </label>
              <input
                id="duration"
                name="duration"
                type="text"
                placeholder="e.g. 6 weeks"
                value={formData.duration}
                onChange={handleChange}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col space-y-1.5">
            <label
              htmlFor="description"
              className="text-xs font-medium text-slate-600"
            >
              Course Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              placeholder="Write a short description about this course..."
              value={formData.description}
              onChange={handleChange}
              className="resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          {/* Footer */}
          <div className="mt-2 flex justify-end gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="inline-flex items-center rounded-full border border-slate-300 bg-white px-4 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-1.5 text-xs font-medium text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? "Saving..." : "Save Course"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCourseModal;
